import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import {
  BusinessRounded,
  CloseRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  EventBusyRounded,
  FestivalRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { designColors } from "services/constants/designTokens";

const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const initialsOf = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);

export const TeamAvailabilityCalendar = ({
  leaveEvents = [],
  companyHolidayEvents = [],
  loading = false,
}) => {
  const today = useMemo(() => dayjs(), []);
  const [cursor, setCursor] = useState(() => today.startOf("month"));
  const [detailsAnchorEl, setDetailsAnchorEl] = useState(null);
  const [selectedDateKey, setSelectedDateKey] = useState("");

  const leaveDateKeys = useMemo(() => {
    const keys = new Set();
    leaveEvents.forEach((event) => {
      const start = dayjs(event.startDate).startOf("day");
      const end = dayjs(event.endDate).startOf("day");
      let cursorDate = start;
      while (
        cursorDate.isValid() &&
        end.isValid() &&
        cursorDate.valueOf() <= end.valueOf()
      ) {
        keys.add(cursorDate.format("YYYY-MM-DD"));
        cursorDate = cursorDate.add(1, "day");
      }
    });
    return keys;
  }, [leaveEvents]);

  const holidayDateKeys = useMemo(
    () =>
      new Set(
        companyHolidayEvents.map((event) =>
          dayjs(event.date).format("YYYY-MM-DD"),
        ),
      ),
    [companyHolidayEvents],
  );

  const eventsByDate = useMemo(() => {
    const map = new Map();

    leaveEvents.forEach((event) => {
      const start = dayjs(event.startDate).startOf("day");
      const end = dayjs(event.endDate).startOf("day");
      let cursorDate = start;
      while (
        cursorDate.isValid() &&
        end.isValid() &&
        cursorDate.valueOf() <= end.valueOf()
      ) {
        const key = cursorDate.format("YYYY-MM-DD");
        const current = map.get(key) || { leaves: [], holidays: [] };
        current.leaves.push(event);
        map.set(key, current);
        cursorDate = cursorDate.add(1, "day");
      }
    });

    companyHolidayEvents.forEach((event) => {
      const key = dayjs(event.date).format("YYYY-MM-DD");
      const current = map.get(key) || { leaves: [], holidays: [] };
      current.holidays.push(event);
      map.set(key, current);
    });

    return map;
  }, [leaveEvents, companyHolidayEvents]);

  const gridDays = useMemo(() => {
    const startOfMonth = cursor.startOf("month");
    const firstColumnIndex = (startOfMonth.day() + 6) % 7;
    const totalCells =
      Math.ceil((firstColumnIndex + cursor.daysInMonth()) / 7) * 7;

    return Array.from({ length: totalCells }, (_, index) => {
      const date = startOfMonth.add(index - firstColumnIndex, "day");
      return {
        date,
        inCurrentMonth: date.month() === cursor.month(),
        isToday: date.isSame(today, "day"),
        onLeave: leaveDateKeys.has(date.format("YYYY-MM-DD")),
        isHoliday: holidayDateKeys.has(date.format("YYYY-MM-DD")),
      };
    });
  }, [cursor, today, leaveDateKeys, holidayDateKeys]);

  const todayOnLeaveMembers = useMemo(
    () =>
      leaveEvents.filter((event) => {
        const start = dayjs(event.startDate).startOf("day");
        const end = dayjs(event.endDate).startOf("day");
        return (
          today.valueOf() >= start.valueOf() && today.valueOf() <= end.valueOf()
        );
      }),
    [leaveEvents, today],
  );

  const visibleOnLeaveMembers = todayOnLeaveMembers.slice(0, 4);
  const overflowCount = todayOnLeaveMembers.length - visibleOnLeaveMembers.length;

  const monthLeaveCount = leaveEvents.filter(
    (event) =>
      dayjs(event.startDate).isSame(cursor, "month") ||
      dayjs(event.endDate).isSame(cursor, "month"),
  ).length;

  const monthHolidayCount = companyHolidayEvents.filter((event) =>
    dayjs(event.date).isSame(cursor, "month"),
  ).length;

  const rowCount = gridDays.length / 7;
  const selectedDateDetails = selectedDateKey
    ? eventsByDate.get(selectedDateKey)
    : null;
  const selectedMonthLabel = cursor.format("MMMM YYYY");
  const selectedDateLabel = selectedDateKey
    ? dayjs(selectedDateKey).format("DD MMM YYYY")
    : "";

  const handleDayClick = (event, dateKey, hasItems, inCurrentMonth) => {
    if (!hasItems || !inCurrentMonth) {
      return;
    }
    setDetailsAnchorEl(event.currentTarget);
    setSelectedDateKey(dateKey);
  };

  const handleCloseDetails = () => {
    setDetailsAnchorEl(null);
    setSelectedDateKey("");
  };

  if (loading) {
    return (
      <Mui.Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          pt: 0.5,
          px: 0.2,
          pb: 0.2,
          borderRadius: 3,
          background:
            "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,1) 100%)",
          border: `1px solid ${designColors.slate100}`,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
        }}
      >
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={0.8}
          sx={{
            mb: 0.75,
            p: 1,
            borderRadius: 2.5,
            background:
              "linear-gradient(135deg, rgba(15,118,110,0.08) 0%, rgba(255,255,255,0.96) 62%)",
            border: "1px solid rgba(15,118,110,0.12)",
          }}
        >
          <Mui.Stack spacing={0.55} sx={{ minWidth: 0 }}>
            <Mui.Skeleton variant="text" width={112} height={18} />
            <Mui.Stack direction="row" alignItems="center" spacing={0.5}>
              <Mui.Skeleton variant="text" width={132} height={26} />
              <Mui.Skeleton variant="circular" width={26} height={26} />
              <Mui.Skeleton variant="circular" width={26} height={26} />
            </Mui.Stack>
            <Mui.Skeleton variant="text" width={176} height={18} />
          </Mui.Stack>

          <Mui.Stack
            direction={{ xs: "row", sm: "column" }}
            alignItems={{ xs: "center", sm: "flex-end" }}
            justifyContent="space-between"
            spacing={0.7}
          >
            <Mui.Stack direction="row" alignItems="center" spacing={0.6}>
              <Mui.Skeleton
                variant="rounded"
                width={88}
                height={24}
                sx={{ borderRadius: 999 }}
              />
              <Mui.Skeleton
                variant="rounded"
                width={96}
                height={24}
                sx={{ borderRadius: 999 }}
              />
            </Mui.Stack>
            <Mui.Skeleton variant="text" width={124} height={18} />
          </Mui.Stack>
        </Mui.Stack>

        <Mui.Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            flexShrink: 0,
            px: 0.2,
          }}
        >
          {weekdayLabels.map((label) => (
            <Mui.Skeleton
              key={label}
              variant="text"
              width="60%"
              height={18}
              sx={{ justifySelf: "center", mb: 0.45 }}
            />
          ))}
        </Mui.Box>

        <Mui.Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gridTemplateRows: `repeat(${rowCount}, 1fr)`,
            gap: 0.45,
            px: 0.2,
          }}
        >
          {Array.from({ length: gridDays.length }).map((_, index) => (
            <Mui.Skeleton
              key={index}
              variant="rounded"
              sx={{
                borderRadius: 2.25,
                transform: "none",
                height: "100%",
                minHeight: 72,
              }}
            />
          ))}
        </Mui.Box>

        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={0.55}
          sx={{
            mt: "auto",
            pt: 0.8,
            px: 0.8,
            pb: 0.55,
            borderTop: `1px solid ${designColors.slate100}`,
          }}
        >
          <Mui.Stack direction="row" alignItems="center" spacing={0.7}>
            <Mui.Stack direction="row" spacing={-0.75}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Mui.Skeleton
                  key={index}
                  variant="circular"
                  width={26}
                  height={26}
                  sx={{ border: `2px solid ${designColors.white}` }}
                />
              ))}
            </Mui.Stack>
            <Mui.Skeleton variant="text" width={120} height={18} />
          </Mui.Stack>

          <Mui.Stack direction="row" spacing={0.5}>
            <Mui.Skeleton
              variant="rounded"
              width={52}
              height={22}
              sx={{ borderRadius: 999 }}
            />
            <Mui.Skeleton
              variant="rounded"
              width={52}
              height={22}
              sx={{ borderRadius: 999 }}
            />
          </Mui.Stack>
        </Mui.Stack>
      </Mui.Box>
    );
  }

  return (
    <Mui.Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        pt: 0.5,
        px: 0.2,
        pb: 0.2,
        borderRadius: 3,
        background:
          "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,1) 100%)",
        border: `1px solid ${designColors.slate100}`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
      }}
    >
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={0.8}
        sx={{
          mb: 0.75,
          p: 1,
          borderRadius: 2.5,
          background:
            "linear-gradient(135deg, rgba(15,118,110,0.08) 0%, rgba(255,255,255,0.96) 62%)",
          border: "1px solid rgba(15,118,110,0.12)",
        }}
      >
        <Mui.Stack spacing={0.45}>
          <Mui.Typography
            sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, color: designColors.tealDark }}
          >
            TEAM CALENDAR
          </Mui.Typography>
          <Mui.Stack direction="row" alignItems="center" spacing={0.1}>
            <Mui.Typography
              sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}
            >
              {selectedMonthLabel}
            </Mui.Typography>
            <Mui.IconButton
              size="small"
              aria-label="Previous month"
              onClick={() => setCursor((prev) => prev.subtract(1, "month"))}
              sx={{
                p: 0.45,
                ml: 0.25,
                border: `1px solid ${designColors.slate200}`,
                backgroundColor: designColors.white,
              }}
            >
              <ChevronLeftRounded sx={{ fontSize: 17 }} />
            </Mui.IconButton>
            <Mui.IconButton
              size="small"
              aria-label="Next month"
              onClick={() => setCursor((prev) => prev.add(1, "month"))}
              sx={{
                p: 0.45,
                border: `1px solid ${designColors.slate200}`,
                backgroundColor: designColors.white,
              }}
            >
              <ChevronRightRounded sx={{ fontSize: 17 }} />
            </Mui.IconButton>
          </Mui.Stack>
          <Mui.Typography sx={{ fontSize: 11.75, color: designColors.slate600 }}>
            Click a marked day to view leave details
          </Mui.Typography>
        </Mui.Stack>

        <Mui.Stack
          direction="column"
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={0.7}
          sx={{ minWidth: 0 }}
        >
          <Mui.Stack direction="row" alignItems="center" spacing={0.6} flexWrap="wrap">
            <Mui.Chip
              icon={<EventBusyRounded sx={{ fontSize: "15px !important" }} />}
              label={`${monthLeaveCount} leave`}
              size="small"
              sx={{
                height: 24,
                backgroundColor: "#E8F7F5",
                color: designColors.tealDark,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
            <Mui.Chip
              icon={<FestivalRounded sx={{ fontSize: "15px !important" }} />}
              label={`${monthHolidayCount} holidays`}
              size="small"
              sx={{
                height: 24,
                backgroundColor: "#FFF4E8",
                color: "#B45309",
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          </Mui.Stack>
          <Mui.Stack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: "wrap", columnGap: 1, rowGap: 0.35 }}
          >
            <Mui.Stack direction="row" alignItems="center" spacing={0.35}>
              <Mui.Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: designColors.teal,
                }}
              />
              <Mui.Typography sx={{ fontSize: 11.25, color: designColors.slate600 }}>
                Leave
              </Mui.Typography>
            </Mui.Stack>
            <Mui.Stack direction="row" alignItems="center" spacing={0.35}>
              <Mui.Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: "#F59E0B",
                }}
              />
              <Mui.Typography sx={{ fontSize: 11.25, color: designColors.slate600 }}>
                Holiday
              </Mui.Typography>
            </Mui.Stack>
            <Mui.Typography sx={{ fontSize: 11.25, fontWeight: 600, color: designColors.slate500 }}>
              {monthLeaveCount + monthHolidayCount} total items
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Stack>
      </Mui.Stack>

      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          flexShrink: 0,
          px: 0.2,
        }}
      >
        {weekdayLabels.map((label) => (
          <Mui.Typography
            key={label}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: label === "Sun" ? "#DC5B3E" : designColors.slate500,
              textAlign: "center",
              pb: 0.45,
              textTransform: "uppercase",
              letterSpacing: 0.35,
            }}
          >
            {label}
          </Mui.Typography>
        ))}
      </Mui.Box>

      <Mui.Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gridTemplateRows: `repeat(${rowCount}, 1fr)`,
          gap: 0.45,
          px: 0.2,
        }}
      >
        {gridDays.map(({ date, inCurrentMonth, isToday, onLeave, isHoliday }) => {
          const isSunday = date.day() === 0;
          const dateKey = date.format("YYYY-MM-DD");
          const dateDetails = eventsByDate.get(dateKey);
          const hasItems = Boolean(dateDetails?.leaves?.length || dateDetails?.holidays?.length);
          const isSelected = selectedDateKey === dateKey;
          const leaveCount = dateDetails?.leaves?.length || 0;
          const holidayCount = dateDetails?.holidays?.length || 0;
          return (
            <Mui.Box
              key={dateKey}
              onClick={(event) =>
                handleDayClick(event, dateKey, hasItems, inCurrentMonth)
              }
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.2,
                minWidth: 0,
                position: "relative",
                overflow: "hidden",
                borderRadius: 2.25,
                p: { xs: 0.35, sm: 0.55 },
                border: `1px solid ${
                  isSelected
                    ? "rgba(15,118,110,0.45)"
                    : isToday
                      ? "rgba(15, 118, 110, 0.35)"
                      : inCurrentMonth
                      ? designColors.slate100
                      : "transparent"
                }`,
                background:
                  isSelected
                    ? "linear-gradient(180deg, rgba(20,184,166,0.18) 0%, rgba(255,255,255,0.98) 100%)"
                    : isToday
                    ? "linear-gradient(180deg, rgba(20,184,166,0.12) 0%, rgba(255,255,255,0.96) 100%)"
                    : isSunday && inCurrentMonth
                      ? "#FFF8F5"
                      : inCurrentMonth
                        ? designColors.white
                        : "rgba(248,250,252,0.45)",
                cursor: hasItems && inCurrentMonth ? "pointer" : "default",
                transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                "&:hover": hasItems && inCurrentMonth
                  ? {
                      transform: "translateY(-1px)",
                      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
                      borderColor: "rgba(15,118,110,0.24)",
                    }
                  : undefined,
              }}
            >
              <Mui.Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.35,
                  minWidth: 0,
                }}
              >
                <Mui.Box
                  sx={{
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: 11, sm: 12 },
                    fontWeight: isToday ? 700 : 500,
                    color: !inCurrentMonth
                      ? designColors.slate300
                      : isToday
                        ? designColors.tealDark
                        : isSunday
                          ? "#C2492F"
                          : designColors.slate800,
                    backgroundColor: isToday ? designColors.tealTint : "transparent",
                    border: isToday
                      ? `1.5px solid ${designColors.tealDark}`
                      : "1.5px solid transparent",
                  }}
                >
                  {date.date()}
                </Mui.Box>
                {hasItems && inCurrentMonth ? (
                  <Mui.Box
                    sx={{
                      // Hidden on phones: the cell is too narrow for a count, and
                      // the leave/holiday dots below already mark the day.
                      minWidth: 18,
                      px: 0.45,
                      height: 16,
                      borderRadius: 999,
                      display: { xs: "none", sm: "flex" },
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: onLeave
                        ? "rgba(20,184,166,0.14)"
                        : "rgba(245,158,11,0.16)",
                      color: onLeave ? designColors.tealDark : "#B45309",
                      fontSize: 9,
                      fontWeight: 800,
                    }}
                  >
                    {leaveCount + holidayCount}
                  </Mui.Box>
                ) : null}
              </Mui.Box>

              <Mui.Box sx={{ minHeight: 18 }}>
                {inCurrentMonth && (onLeave || isHoliday) ? (
                  <Mui.Box
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      justifyContent: "center",
                      gap: 0.4,
                      pt: 0.2,
                    }}
                  >
                    {onLeave ? (
                      <Mui.Box
                        sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: designColors.teal }}
                      />
                    ) : null}
                    {isHoliday ? (
                      <Mui.Box
                        sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#F59E0B" }}
                      />
                    ) : null}
                  </Mui.Box>
                ) : null}
                {inCurrentMonth && (onLeave || isHoliday) ? (
                  <Mui.Stack spacing={0.18}>
                    {onLeave ? (
                      <Mui.Typography
                        sx={{
                          display: { xs: "none", sm: "block" },
                          fontSize: 9.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 700,
                          color: designColors.tealDark,
                          lineHeight: 1.1,
                          letterSpacing: 0.1,
                        }}
                      >
                        {leaveCount > 1 ? `${leaveCount} leaves` : "Leave"}
                      </Mui.Typography>
                    ) : null}
                    {isHoliday ? (
                      <Mui.Typography
                        sx={{
                          display: { xs: "none", sm: "block" },
                          fontSize: 9.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 700,
                          color: "#B45309",
                          lineHeight: 1.1,
                          letterSpacing: 0.1,
                        }}
                      >
                        {holidayCount > 1 ? `${holidayCount} holidays` : "Holiday"}
                      </Mui.Typography>
                    ) : null}
                  </Mui.Stack>
                ) : null}
              </Mui.Box>
            </Mui.Box>
          );
        })}
      </Mui.Box>

      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={0.55}
        sx={{
          mt: "auto",
          pt: 0.8,
          px: 0.8,
          pb: 0.55,
          borderTop: `1px solid ${designColors.slate100}`,
        }}
      >
        <Mui.Stack direction="row" alignItems="center" spacing={0.7}>
          <Mui.Stack
            direction="row"
            sx={{ "& > *": { ml: "-6px" }, "& > *:first-of-type": { ml: 0 } }}
          >
            {visibleOnLeaveMembers.map((member) => (
              <Mui.Avatar
                key={`${member.name}-${member.id}`}
                src={member.imageUrl}
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: 11.5,
                  fontWeight: 700,
                  backgroundColor: designColors.tealTint,
                  color: designColors.tealDark,
                  border: `2px solid ${designColors.white}`,
                }}
              >
                {initialsOf(member.name)}
              </Mui.Avatar>
            ))}
          </Mui.Stack>
          {overflowCount > 0 ? (
            <Mui.Chip
              label={`+${overflowCount}`}
              size="small"
              sx={{
                height: 22,
                fontWeight: 700,
                backgroundColor: designColors.slate100,
                color: designColors.slate700,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          ) : null}
          <Mui.Typography sx={{ fontSize: 12, fontWeight: 500, color: designColors.slate700 }}>
            {todayOnLeaveMembers.length > 0
              ? `${todayOnLeaveMembers.length} on leave today`
              : "No leave today"}
          </Mui.Typography>
        </Mui.Stack>

        <Mui.Stack direction="row" spacing={0.5}>
          <Mui.Chip
            icon={<BusinessRounded sx={{ fontSize: "15px !important" }} />}
            label={monthLeaveCount}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              borderColor: designColors.slate200,
              color: designColors.slate700,
              backgroundColor: designColors.white,
            }}
          />
          <Mui.Chip
            icon={<FestivalRounded sx={{ fontSize: "15px !important" }} />}
            label={monthHolidayCount}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              borderColor: designColors.slate200,
              color: designColors.slate700,
              backgroundColor: designColors.white,
            }}
          />
        </Mui.Stack>
      </Mui.Stack>

      {loading ? (
        <Mui.Box sx={{ pt: 0.5 }}>
          <Mui.LinearProgress
            sx={{
              height: 4,
              borderRadius: 999,
              backgroundColor: designColors.slate100,
              "& .MuiLinearProgress-bar": {
                backgroundColor: designColors.teal,
              },
            }}
          />
        </Mui.Box>
      ) : null}

      <Mui.Popover
        open={Boolean(detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={handleCloseDetails}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 3,
            p: 1.25,
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
            border: `1px solid ${designColors.slate100}`,
            background:
              "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
          },
        }}
      >
        <Mui.Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Mui.Box>
            <Mui.Typography
              sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}
            >
              {selectedDateLabel || "Leave details"}
            </Mui.Typography>
            <Mui.Typography sx={{ fontSize: 12, color: designColors.slate600 }}>
              Team availability details for the selected day
            </Mui.Typography>
          </Mui.Box>
          <Mui.IconButton size="small" onClick={handleCloseDetails} sx={{ p: 0.4 }}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </Mui.IconButton>
        </Mui.Stack>

        <Mui.Stack spacing={0.9}>
          {(selectedDateDetails?.leaves || []).map((leave) => (
            <Mui.Box
              key={`leave-${leave.id}-${selectedDateKey}`}
              sx={{
                border: `1px solid ${designColors.slate100}`,
                borderRadius: 2,
                p: 1,
                backgroundColor: designColors.white,
              }}
            >
              <Mui.Stack direction="row" spacing={1} alignItems="center">
                <Mui.Avatar
                  src={leave.imageUrl}
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: 12.5,
                    fontWeight: 700,
                    backgroundColor: designColors.tealTint,
                    color: designColors.tealDark,
                  }}
                >
                  {initialsOf(leave.name)}
                </Mui.Avatar>
                <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                  <Mui.Typography
                    sx={{ fontSize: 13.5, fontWeight: 700, color: designColors.slate900 }}
                  >
                    {leave.name}
                  </Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 11.75, color: designColors.slate600 }}>
                    {leave.role} - {leave.department}
                  </Mui.Typography>
                </Mui.Box>
                <Mui.Chip
                  label={leave.type}
                  size="small"
                  sx={{
                    height: 22,
                    backgroundColor: `${leave.color}20`,
                    color: leave.color,
                    fontWeight: 700,
                  }}
                />
              </Mui.Stack>

              <Mui.Stack spacing={0.35} sx={{ mt: 0.9 }}>
                <Mui.Typography sx={{ fontSize: 12, color: designColors.slate700 }}>
                  {dayjs(leave.startDate).isSame(dayjs(leave.endDate), "day")
                    ? dayjs(leave.startDate).format("DD MMM YYYY")
                    : `${dayjs(leave.startDate).format("DD MMM YYYY")} - ${dayjs(
                        leave.endDate,
                      ).format("DD MMM YYYY")}`}
                </Mui.Typography>
                <Mui.Typography sx={{ fontSize: 12, color: designColors.slate600 }}>
                  {leave.halfDay
                    ? `Half day - ${
                        leave.whichHalf === "1st" ? "Morning" : "Afternoon"
                      }`
                    : `${leave.businessDays || 1} day${
                        leave.businessDays > 1 ? "s" : ""
                      }`}
                </Mui.Typography>
              </Mui.Stack>
            </Mui.Box>
          ))}

          {(selectedDateDetails?.holidays || []).map((holiday) => (
            <Mui.Box
              key={`holiday-${holiday.id}-${selectedDateKey}`}
              sx={{
                border: "1px solid #FDE7C2",
                borderRadius: 2,
                p: 1,
                backgroundColor: "#FFF9F0",
              }}
            >
              <Mui.Stack direction="row" spacing={1} alignItems="center">
                <Mui.Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#FDE7C2",
                    color: "#B45309",
                  }}
                >
                  <FestivalRounded sx={{ fontSize: 18 }} />
                </Mui.Box>
                <Mui.Box>
                  <Mui.Typography
                    sx={{ fontSize: 13.5, fontWeight: 700, color: "#92400E" }}
                  >
                    {holiday.label}
                  </Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 12, color: designColors.slate600 }}>
                    Company holiday
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Stack>
            </Mui.Box>
          ))}
        </Mui.Stack>
      </Mui.Popover>
    </Mui.Box>
  );
};
